import { Component, inject, OnInit, signal, computed, effect } from '@angular/core';

import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { GlobalMetaDataEntityService, GlobalMetaDataEntityState } from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../../global-metadata/global-metadata.entity';
import { TranslocoModule } from '@jsverse/transloco';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GenericTreeHighlightService } from '../services/generic-tree-highlight.service';

@Component({
  selector: 'pixl-metadata-level-assignment-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslocoModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './metadata-level-assignment-dialog.component.html',
  styleUrl: './metadata-level-assignment-dialog.component.scss'
})
export class MetadataLevelAssignmentDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<MetadataLevelAssignmentDialogComponent>);
  public data = inject(MAT_DIALOG_DATA) as {
    maxDepth: number
    activeMetadataByLevel: Record<number, string[]>
    saveFn: (result: { level: number, metadata: GlobalMetaDataEntity[] }) => Promise<void>
  };

  private store = inject(Store);
  private globalMetadataService = inject(GlobalMetaDataEntityService);
  private highlightService = inject(GenericTreeHighlightService);

  levels = computed(() => Array.from({ length: this.data.maxDepth }, (_, i) => i + 1));
  selectedLevel = signal<number | null>(null);
  isLoading = signal(false);

  allMetadata = signal<GlobalMetaDataEntity[]>([]);
  activeMetadata = signal<GlobalMetaDataEntity[]>([]);

  constructor() {
    effect(() => {
      const lvl = this.selectedLevel();
      const all = this.allMetadata();

      this.highlightService.setFocusedLevel(lvl);

      if (lvl && all.length > 0) {
        const activeIds = this.data.activeMetadataByLevel[lvl] || [];
        const activeModels = all.filter(m => activeIds.includes(m.id));
        this.activeMetadata.set(activeModels);
      }
      else {
        this.activeMetadata.set([]);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadMetadata();
  }

  async loadMetadata() {
    await this.globalMetadataService.ensureLoaded({ page: { size: 1000 } });
    const entities = this.store.selectSnapshot(GlobalMetaDataEntityState.all);

    const models = [...entities];
    models.sort((a, b) => a.name.localeCompare(b.name));
    this.allMetadata.set(models);
  }

  isActive(meta: GlobalMetaDataEntity): boolean {
    return this.activeMetadata().some(m => m.id === meta.id);
  }

  toggleMetadata(meta: GlobalMetaDataEntity) {
    if (!this.selectedLevel()) {
      return;
    }

    if (this.isActive(meta)) {
      this.activeMetadata.update(list => list.filter(m => m.id !== meta.id));
    }
    else {
      this.activeMetadata.update(list => {
        const newList = [...list, meta];
        return newList.sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }

  onLevelHover(level: number): void {
    this.highlightService.setFocusedLevel(level);
  }

  onLevelBlur(): void {
    // revert to selected
    this.highlightService.setFocusedLevel(this.selectedLevel());
  }

  async onSave(): Promise<void> {
    if (!this.selectedLevel()) return;

    this.isLoading.set(true);
    try {
      await this.data.saveFn({
        level: this.selectedLevel()!,
        metadata: this.activeMetadata()
      });
      this.dialogRef.close(true);
    }
    catch (error) {
      console.error('Error saving metadata assignment:', error);
    }
    finally {
      this.isLoading.set(false);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
